import { getAddresses } from '@iheartjane/geolocation';
import {
  Flex,
  LocationPinFilledIcon,
  TextField,
  Typography,
} from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import debounce from 'lodash-es/debounce';
import { useEffect, useState } from 'react';

import { getGoogleMaps } from '@jane/shared-ecomm/util';

import type { AddressAutocompleteProps } from './addressAutocomplete.types';
import { GoogleFooter } from './googleFooter';

export const AddressAutocomplete = ({
  'aria-label': ariaLabel,
  autoFocus,
  clearIcon,
  countries = ['us', 'ca', 'pr'],
  'data-testid': testId,
  defaultValue = '',
  disableMobileInputStyling = false,
  errorMessage,
  name,
  isForm,
  label,
  labelHidden,
  onBlur,
  onChange,
  onSelection,
  locationIcon,
  placeholder,
}: AddressAutocompleteProps) => {
  const [options, setOptions] = useState<string[]>([]);

  const TextFieldComponent = isForm ? Form.TextField : TextField;

  const handleGetAddresses = debounce((value) => {
    getAddresses({
      countries,
      input: value,
      loader: getGoogleMaps,
    }).then((result) =>
      setOptions(
        result.map(
          (location: { label: string; value: string }) => location.value
        )
      )
    );
  }, 200);

  const handleOnChange = (value: string) => {
    if (value.length > 0) {
      handleGetAddresses(value);
    } else {
      setOptions([]);
    }

    onChange && onChange(value);
  };

  useEffect(() => {
    if (defaultValue.length > 0) {
      handleGetAddresses(defaultValue);
    }
  }, [defaultValue]);

  return (
    <Flex width="100%" flexDirection="column">
      <TextFieldComponent
        aria-label={ariaLabel}
        aria-describedby={errorMessage ? 'address-error' : undefined}
        autocompleteFooter={<GoogleFooter />}
        autocompleteOptions={options}
        autoFocus={autoFocus}
        clearIcon={clearIcon}
        defaultValue={defaultValue}
        disableMobileInputStyling={disableMobileInputStyling}
        label={label}
        labelHidden={labelHidden}
        name={name}
        onBlur={onBlur}
        onChange={handleOnChange}
        onSelection={onSelection}
        placeholder={placeholder}
        required
        startIcon={
          locationIcon && <LocationPinFilledIcon size="sm" color="grays-mid" />
        }
        data-testid={testId}
        width="100%"
      />
      {errorMessage && (
        <Typography mt={16} color="error" id="address-error">
          {errorMessage}
        </Typography>
      )}
    </Flex>
  );
};
